import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export class MultiTranslateHttpLoader implements TranslateLoader {
    constructor(
        private http: HttpClient,
        private prefix: string = './assets/i18n/',
        private suffix: string = '.json',
        private sections: string[] = []
    ) { }

    /**
     * Loads the translations for a given language.
     * @param lang The language to load.
     * @returns An observable that resolves to an object containing the translations.
     */
    public getTranslation(lang: string): Observable<any> {
        const requests = this.sections.map((section) =>
            this.http.get(`${this.prefix}${lang}/${section}${this.suffix}`).pipe(
                catchError((error) => {
                    console.error(`Could not find translation file: ${this.prefix}${lang}/${section}${this.suffix}`, error);
                    return of({});
                })
            )
        );

        //  ForkJoin will wait for all requests to complete and then combine their results into a single object
        return forkJoin(requests).pipe(
            map((translations) => {
                return translations.reduce((acc, current) => {
                    return this.mergeDeep(acc, current);
                }, {});
            })
        );
    }

    /**
     * Merges two objects into a single object, recursively merging nested objects.
     * If a key exists in both objects, the value from the source object will be used.
     * If a key only exists in the target object, its value will be preserved.
     * If a key only exists in the source object, its value will be added to the target object.
     * @param target The object to merge into.
     * @param source The object to merge from.
     * @returns The merged object.
     */
        private mergeDeep(target: any, source: any): any {
        const output = Object.assign({}, target);
        if (this.isObject(target) && this.isObject(source)) {
            Object.keys(source).forEach((key) => {
                if (this.isObject(source[key])) {
                    if (!(key in target)) {
                        Object.assign(output, { [key]: source[key] });
                    } else {
                        output[key] = this.mergeDeep(target[key], source[key]);
                    }
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }
        return output;
    }

    /**
     * Checks if a given item is an object (not an array).
     * @param item The item to check.
     * @returns True if the item is an object, false otherwise.
     */
    private isObject(item: any): boolean {
        return item && typeof item === 'object' && !Array.isArray(item);
    }
}
